/* eslint-disable no-use-before-define */
/* eslint-disable no-else-return */
import _ from 'underscore';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { firstValue, getInitializerValues } from '../initializers';
import LifecycleEvent from '../lifecycle-event';
import useLazyState from '../use-lazy-state';

export default function usePaymentInfoState({ account, onStateChange, initializers }) {
  const { defaults, overrides } = getInitializerValues(_.omit(initializers, 'isLoading'));

  const [country, setCountry, countrySettled] = useLazyState(firstValue(overrides.country, getCountry(account), defaults.country), !!account);
  const [paymentType, setPaymentType, paymentTypeSettled] = useLazyState(firstValue(overrides.paymentType, getPaymentType(account), defaults.paymentType), !!account && !initializers.isLoading);
  const [piid, setPiid, piidSettled] = useLazyState(firstValue(overrides.piid, defaults.piid), !initializers.isLoading);
  const [piType, setPiType, piTypeSettled] = useLazyState(firstValue(overrides.paymentMethod, defaults.paymentMethod), !initializers.isLoading);
  const [amount, setAmount] = useState(firstValue(overrides.amount, defaults.amount));
  const [autorecharge, setAutorecharge] = useState(firstValue(overrides.autorecharge, defaults.autorecharge));
  const [couponCode, setCouponCode] = useState(firstValue(overrides.couponCode, defaults.couponCode));
  const [piValidation, setPiValidation] = useState(true);

  // TODO: maybe locally persist payment settings, in case we need to resume after a reload and the final state cannot be inferred from the server data?
  // NOTE: state.isLoading depends on paymentSettings.isLoading, which depends on dataRestrictions.isLoading, which (indirectly) depends on the account being loaded
  const isLoading = !countrySettled || !paymentTypeSettled || !piidSettled || !piTypeSettled;
  const onTermsChange = useStateChange({
    onStateChange,
    couponCode,
    country,
    paymentType,
    piid,
    piType,
    amount,
    autorecharge,
    piValidation,
    isLoading,
  });

  const state = useMemo(() => ({
    couponCode,
    country,
    paymentType,
    piid,
    piType,
    amount,
    autorecharge,
    isLoading,
  }), [couponCode, country, paymentType, piid, piType, amount, autorecharge, isLoading]);

  const setState = useMemo(() => ({
    couponCode: setCouponCode,
    country: setCountry,
    paymentType: setPaymentType,
    piid: setPiid,
    piType: setPiType,
    amount: setAmount,
    autorecharge: setAutorecharge,
    piValidation: setPiValidation,
    terms: onTermsChange,
  }), [setCouponCode, setCountry, setPaymentType, setPiid, setPiType, setAmount, setAutorecharge, onTermsChange]);

  return [state, setState];
}

function useStateChange({
  onStateChange,
  couponCode,
  country,
  paymentType,
  piid,
  piType,
  amount,
  autorecharge,
  piValidation,
  isLoading,
}) {
  const dataRef = useRef({ terms: {} });
  dataRef.current = {
    ...dataRef.current,
    couponCode,
    country,
    paymentType,
    piid,
    piType,
    amount,
    autorecharge,
    piValidation,
  };

  const onTermsChange = useCallback(({ accepted, communication }) => {
    const newData = {
      ...dataRef.current,
      terms: {
        accepted,
        communication: {
          // make sure non-nullable properties are always sent to the server
          AllowContact: false,
          ...communication,
        },
      },
    };

    dataRef.current = newData;
    onStateChange({ type: LifecycleEvent.DataChanged, args: { data: newData } });
  }, [onStateChange]);

  useEffect(() => {
    // NOTE: the event should fire every time any of the data properties change,
    // except for the "terms" property (which is handled separately)
    onStateChange({ type: LifecycleEvent.DataChanged, args: { data: dataRef.current } });
  }, [couponCode, country, paymentType, piid, piType, amount, autorecharge, piValidation, onStateChange]);

  useEffect(() => {
    // NOTE: the expectation is this should fire exactly once, when we finished all direct load operations
    // (there might be additional loading going on, intermittently, through various internal components,
    // but due to their intermittent nature, those are not suitable to be included in this signal)
    if (!isLoading) {
      onStateChange({ type: LifecycleEvent.DataLoaded });
    }
  }, [isLoading, onStateChange]);

  return onTermsChange;
}

function getCountry(account) {
  return _.chain(account).result('BusinessAddress').result('Country').value();
}

function getPaymentType(account) {
  const originalPaymentType = _.chain(account).result('PaymentType').value();
  return originalPaymentType === Constants.PaymentTypeId.Undefined ? undefined : originalPaymentType;
}
